import { useInvoiceSnapshotCount } from '@Hooks/Billing';
import { useArchivePreview } from '@Hooks/Documents';
import {
    Button,
    DateInput,
    defaultIconSize,
    FlexColumn,
    FlexRow,
    type IApiMessage,
    InlineBanner,
    Modal,
    RadioButton,
    RadioCardGroup,
    type RadioCardOption,
} from '@synqo/frontend-component-library';
import { IconAlertTriangle, IconDownload, IconInfoCircle } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import styles from './InvoiceSnapshotExportModal.module.css';
import {
    buildSelection,
    describeInvoiceCount,
    type ExportFormat,
    type ExportScope,
    inputsOfPreset,
    type PeriodPreset,
    periodOfInputs,
    presetOfPeriod,
    summarizeExport,
} from './invoiceSnapshotExport';
import { INVOICE_SNAPSHOT_CATEGORY } from './useInvoiceSnapshotDocument';
import { type ExportOutcome, type StartExportInput } from './useInvoiceSnapshotExport';

const PRESETS: RadioCardOption<PeriodPreset>[] = [
    { value: 'lastMonth', title: 'Letzter Monat' },
    { value: 'lastQuarter', title: 'Letztes Quartal' },
    { value: 'currentYear', title: 'Laufendes Jahr' },
    { value: 'custom', title: 'Benutzerdefiniert' },
];

const NO_MESSAGES: IApiMessage[] = [];

export interface InvoiceSnapshotExportModalProps {
    onClose: () => void;
    selectedIds: string[];
    /** The ids of the rows the table has loaded, which is what "current view" means. */
    viewIds: string[];
    /** The overview export definition, or undefined when this session is offered none. */
    excelDefinitionId: string | undefined;
    canDownloadArchive: boolean;
    startExport: (input: StartExportInput) => Promise<ExportOutcome>;
    isStarting: boolean;
    isWaiting: boolean;
    /** Called once an export was handed to the background, so the list can drop its selection. */
    onExportStarted: () => void;
}

/** Asks which invoices to export in which format, and shows count, size and blockers before the start. */
// @ai-generated 2026-09-29T07:37:25+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function InvoiceSnapshotExportModal({
    onClose,
    selectedIds,
    viewIds,
    excelDefinitionId,
    canDownloadArchive,
    startExport,
    isStarting,
    isWaiting,
    onExportStarted,
}: InvoiceSnapshotExportModalProps) {
    const hasSelectedRows = selectedIds.length > 0;

    const [scope, setScope] = useState<ExportScope>(hasSelectedRows ? 'selection' : 'period');
    const [format, setFormat] = useState<ExportFormat>(canDownloadArchive ? 'zip' : 'excel');
    const [inputs, setInputs] = useState(() => inputsOfPreset('lastMonth')!);
    const [isCustom, setIsCustom] = useState(false);

    const period = useMemo(() => periodOfInputs(inputs.from, inputs.to), [inputs]);
    const preset = isCustom || period === undefined ? 'custom' : presetOfPeriod(period);

    const selection = useMemo(
        () => buildSelection({ scope, period, selectedIds, viewIds }),
        [scope, period, selectedIds, viewIds],
    );

    // The figures of both formats load at once, so switching the format shows them right away.
    const {
        preview,
        messages: archiveMessages,
        isLoading: isPreviewing,
    } = useArchivePreview(INVOICE_SNAPSHOT_CATEGORY, canDownloadArchive ? selection : undefined);
    const { count: invoiceCount, isLoading: isCounting } = useInvoiceSnapshotCount(
        scope === 'period' ? selection : undefined,
    );

    const isZip = format === 'zip';
    const listedCount = selection?.include_ids?.length || undefined;
    const documentCount = isZip ? preview?.printouts.length : invoiceCount ?? listedCount;
    const messages = isZip ? archiveMessages : NO_MESSAGES;
    const isLoading = isZip ? isPreviewing : isCounting;
    const exportSummary = useMemo(
        () =>
            summarizeExport({
                format,
                scope,
                period,
                documentCount,
                totalFileSize: preview?.total_file_size ?? 0,
                printouts: preview?.printouts ?? [],
                messages,
                hasSelection: selection !== undefined,
                isLoading,
            }),
        [format, scope, period, documentCount, preview, messages, selection, isLoading],
    );

    // While new figures load, the last finished summary stays, so a changed choice does not blank the notice.
    // Compared by content: the summary is new on every render.
    const summaryKey = JSON.stringify(exportSummary);
    const [settled, setSettled] = useState({ key: summaryKey, summary: exportSummary });
    if (!isLoading && settled.key !== summaryKey) {
        setSettled({ key: summaryKey, summary: exportSummary });
    }
    const { notices } = isLoading ? settled.summary : exportSummary;

    const choosePreset = (value: PeriodPreset) => {
        setIsCustom(value === 'custom');
        const filled = inputsOfPreset(value);
        if (filled !== undefined) {
            setInputs(filled);
        }
        return true;
    };

    const start = async () => {
        if (selection === undefined) {
            return;
        }

        const outcome = await startExport({ format, selection, documentCount, excelDefinitionId });
        if (outcome === 'refused' || outcome === 'waiting') {
            return;
        }

        onExportStarted();
        onClose();
    };

    const isBusy = isStarting || isWaiting;

    return (
        <Modal
            isOpen
            onClose={onClose}
            title="Rechnungen exportieren"
            footer={
                <FlexRow justify="end" className="pt-3">
                    <Button appearance="outlined" onClick={onClose}>
                        Abbrechen
                    </Button>
                    <Button
                        appearance="filled"
                        variant="success"
                        onClick={start}
                        locked={isBusy || !exportSummary.canStart}
                    >
                        <IconDownload size={defaultIconSize} />
                        &nbsp;{isBusy ? 'Export läuft' : 'Exportieren'}
                    </Button>
                </FlexRow>
            }
        >
            <FlexColumn justify="start" align="stretch" className={styles.content}>
                <span className={styles.heading}>Umfang</span>
                <ScopeOption scope="period" value={scope} label="Zeitraum" onChoose={setScope} />
                {scope === 'period' && (
                    <FlexColumn justify="start" align="stretch" className={styles.periodControls}>
                        <RadioCardGroup<PeriodPreset>
                            name="preset"
                            label="Zeitraum wählen"
                            layout="chip"
                            options={PRESETS}
                            value={preset}
                            onChange={choosePreset}
                        />
                        <FlexRow justify="start" className={styles.dateRange}>
                            <DateInput
                                type="date"
                                name="from"
                                value={inputs.from}
                                onChange={(value) => {
                                    setInputs((current) => ({ ...current, from: value }));
                                    return true;
                                }}
                                required
                            />
                            <span className={styles.muted}>bis</span>
                            <DateInput
                                type="date"
                                name="to"
                                value={inputs.to}
                                onChange={(value) => {
                                    setInputs((current) => ({ ...current, to: value }));
                                    return true;
                                }}
                                required
                            />
                        </FlexRow>
                    </FlexColumn>
                )}
                {hasSelectedRows && (
                    <ScopeOption
                        scope="selection"
                        value={scope}
                        label={`Aktuelle Auswahl (${describeInvoiceCount(selectedIds.length)})`}
                        onChoose={setScope}
                    />
                )}
                <ScopeOption scope="view" value={scope} label="Aktuelle Ansicht" onChoose={setScope} />
                <span className={`${styles.muted} ${styles.scopeDetail}`}>
                    übernimmt Modus („Rechnungen“ / „Offene Posten“) und Suchfilter
                </span>

                <span className={styles.heading}>Format</span>
                <RadioCardGroup<ExportFormat>
                    name="format"
                    label="Format"
                    options={[
                        {
                            value: 'zip',
                            title: 'PDF-Sammel-Download (ZIP)',
                            description: 'Alle Rechnungs-PDFs des Umfangs, z. B. für den Steuerberater',
                            locked: !canDownloadArchive,
                        },
                        {
                            value: 'excel',
                            title: 'Excel-Tabelle (.xlsx)',
                            description: 'Kopfdaten, eine Zeile pro Rechnung, für den Abgleich mit der Buchhaltung',
                            locked: excelDefinitionId === undefined,
                        },
                    ]}
                    value={format}
                    onChange={(value) => {
                        setFormat(value);
                        return true;
                    }}
                />

                <div className={styles.notices}>
                    {isWaiting ? (
                        <InlineBanner
                            variant="info"
                            size="sm"
                            icon={<IconInfoCircle size={defaultIconSize} />}
                            title="Ihr Export wird erstellt …"
                            description="Der Download startet, sobald er fertig ist."
                        />
                    ) : (
                        notices.map((notice) => (
                            <InlineBanner
                                key={notice.title}
                                variant={notice.tone}
                                size="sm"
                                icon={
                                    notice.tone === 'warning' ? (
                                        <IconAlertTriangle size={defaultIconSize} />
                                    ) : (
                                        <IconInfoCircle size={defaultIconSize} />
                                    )
                                }
                                title={notice.title}
                                description={notice.description}
                            />
                        ))
                    )}
                </div>
            </FlexColumn>
        </Modal>
    );
}

interface ScopeOptionProps {
    scope: ExportScope;
    value: ExportScope;
    label: string;
    onChoose: (scope: ExportScope) => void;
}

// @ai-generated 2026-09-29T07:37:25+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
function ScopeOption({ scope, value, label, onChoose }: ScopeOptionProps) {
    return (
        <RadioButton
            name={`scope-${scope}`}
            label={label}
            value={value === scope}
            onChange={(checked) => {
                if (checked) {
                    onChoose(scope);
                }
                return true;
            }}
        />
    );
}
